<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, ref, reactive, computed, nextTick } from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { IPatientDemographicsResult } from '../../lib/Models/PatientModel';
    import { IMMLKeyListItem, IMMLAuditPrintInputs, IMMLDeleteKeyInputs, IMMLAuditListInputs, IMMLAuditListItem, 
        MMLAdminConnectionType, IMMLSendTokenInputs, IMMLCreateAccountInputs, MMLAdminSendType, IMMLSendKeyInputs, 
        IMMLCreateKeyOnlyInputs, MMLKeyListRecordType } from '../../lib/Models/MMLModel';
    import { ICol, IRowSelection, calculateGridRowCount, ValueStrDelegate } from "../../lib/ChoiceTableHelpers";
    import { ButtonDisplaySize, ModalSize, TableRowHighlight, TableRowType, IconTypes, DisplayStyle } from "../../Enums/UiEnums";
    import { AppScreens, PortalType, CompanySettingParameterNames, MMLConnectionType, CreditCardFraudIndicator } from "../../Enums/ChoiceEnums";
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { popAlert, ModalStyles } from "../../lib/Notification";
    import { getCompanySetting } from '../../lib/Api/Claim';
    import { openWindowFromVue } from '../../lib/Models/CommonItemsModel'; 
    import { MML } from '../../lib/ChoiceApi';   
    import ResizeMonitor from '../../components/ResizeMonitor.vue'
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'
    import ModalDialog from '../../components/ModalDialog.vue'
    import TextInput from "../../components/TextInput.vue";
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import PhoneInput from "../../components/Inputs/PhoneInput.vue";
    import MMLIcon from "../../components/Choice/Patient/MMLIcon.vue";
    import EmailInput from '../../components/Inputs/EmailInput.vue';
    import ChoiceStateMessageSink from '../../components/ChoiceStateMessageSink.vue';
    import * as Validation from '../../lib/Validation';
    import * as Conversions from "../../lib/Conversions";

    const privilegesStore = usePrivilegesStore(initPinia());
    const createEmailInput = ref();
    const createPhoneInput = ref();
    const generateEmailInput = ref();
    const generatePhoneInput = ref();

    const props = defineProps<{
        demographics: IPatientDemographicsResult,
        keyList: IMMLKeyListItem[],
        connectionType: MMLConnectionType
    }>(
    );

    const formData = reactive<{
        connectionType?: MMLConnectionType,
        keyList: IMMLKeyListItem[],
        windowHeight: number,
        currentRow?: IMMLKeyListItem,
        letterTemplateId?: number,
        letterInstructionsId?: number,
        showReason: boolean,
        deleteKeyReason: string,
        showAudit: boolean,
        auditList: IMMLAuditListItem[],
        showVerbalConnect: boolean,
        verbalConnect: boolean,
        showConnectPatient: boolean,
        requestedBy?: string,
        adminConnectionType: MMLAdminConnectionType,
        createEmail?: string,
        createPhone?: string,
        generateEmail?: string,
        generatePhone?: string,
        showTokenEntry: boolean,
        token?: number,
        createdMMLKey?: string,
        showPrintInstructions: boolean,
        showCreditCardFraudDialog: boolean
    }>({
        keyList: [],
        windowHeight: 100,
        showReason: false,
        deleteKeyReason: "",
        showAudit: false,
        auditList: [],
        showVerbalConnect: false,
        verbalConnect: false,
        showConnectPatient: false,
        adminConnectionType: MMLAdminConnectionType.CreateEmail,
        showTokenEntry: false,
        showPrintInstructions: false,
        showCreditCardFraudDialog: false
    });

    const keyColumns = ref<ICol[]>([
        { Name: 'Key', DataField: 'mmlKey' },
        { Name: 'Requested By', DataField: 'requestedBy' },
        { Name: 'Date', DataField: 'postedDate' },
        { Name: 'Status', DataField: 'status' },
    ]);

    const auditColumns = ref<ICol[]>([
        { Name: 'Description', DataField: 'description' },
        { Name: 'Old Value', DataField: 'oldValue' },
        { Name: 'New Value', DataField: 'newValue' },
        { Name: 'User ID', DataField: 'userId' },
        { Name: 'Posted', DataField: 'postedDate' },
    ]);

    async function fetchData(){
        const promisePrivilege = privilegesStore.getScreens([AppScreens.MMLAdmin]);
        const promiseLetterTemplateId = getCompanySetting(CompanySettingParameterNames.MyMedicalLocker,7);
        const promiseLetterInstructionsId = getCompanySetting(CompanySettingParameterNames.MyMedicalLocker,11);
        await Promise.all([promisePrivilege, promiseLetterTemplateId, promiseLetterInstructionsId]).then(function(data) {
            if (data[1] != "")
                formData.letterTemplateId = parseInt(data[1], 10);
            if (data[2] != "")
                formData.letterInstructionsId = parseInt(data[2], 10);
        });
    }
    fetchData();

    onMounted(async () => {
        formData.connectionType = props.connectionType;
        formData.keyList = props.keyList;
        windowResize();
    });

    const patientMiddleInitial = computed<string>(() => props.demographics.middleName ? ` ${props.demographics.middleName.substring(0,1)}.` : "");
    const patientBirthDate = computed<string>(() => props.demographics.birthDate ? ` ${props.demographics.birthDate}` : "");
    const patientGender = computed<string>(() => props.demographics.genderDesc ? ` ${props.demographics.genderDesc}` : "");

    const connectButtonText = computed<string>(() => {
        const options = ["Email Token", "Text Token", "Email Connection Key", "Text Connection Key", "Print"];
        return options[formData.adminConnectionType];
    });
    const connectButtonSpinText = computed<string>(() => {
        const options = ["Emailing Token", "Texting Token", "Emailing Connection Key", "Texting Connection Key", "Printing"];
        return options[formData.adminConnectionType];
    });
    const connectButtonTooltip = computed<string>(() => {
        const options = ["Email the token to the address.", "Text the token to the phone number.", "Email the connection key to the address.", "Text the connection key to the phone number.", "Create a new key and print the MyMedicalLocker Welcome Letter to be given to the patient."];
        return options[formData.adminConnectionType];
    });
    const informationSentTo = computed<string>(() => {
        let sentTo = "";
        switch (formData.adminConnectionType) {
            case MMLAdminConnectionType.CreateEmail: sentTo = formData.createEmail ?? ""; break;
            case MMLAdminConnectionType.CreatePhone: sentTo = formData.createPhone ?? ""; break;
            case MMLAdminConnectionType.GenerateEmail: sentTo = formData.generateEmail ?? ""; break;
            case MMLAdminConnectionType.GeneratePhone: sentTo = formData.generatePhone ?? ""; break;
            case MMLAdminConnectionType.GenerateLetter: sentTo = ""; break;
            default: break;
        }
        return sentTo;
    });
    const keyButtonDisabled = computed<boolean>(() => {
        if (!formData.currentRow)
            return true;
        if (!formData.currentRow.mmlKey)
            return true;
        return false;
    });

    const hasFraud = computed(() => formData.keyList.some(item => item.ccFraudInd === CreditCardFraudIndicator.IsFraudulent));
    const fraudRecord = computed(() => formData.keyList.find(item => item.ccFraudInd === CreditCardFraudIndicator.IsFraudulent));

    function windowResize() {
        formData.windowHeight = window.innerHeight - 90;
    }

    function onRowClick(rowData: IRowSelection,  _rowType: TableRowType, _evt: MouseEvent) : void {
        formData.currentRow = <IMMLKeyListItem> rowData;
    }

    async function onPrintKey() {
        if (!isLetterConfigured())
            return;

        if (!formData.currentRow) {
            popAlert("Please select a key to print","Print Key");
            return;
        }
        if (formData.currentRow.mmlKey.length != 11)
        {
            popAlert("This is an old key and cannot be used anymore to connect a patient to a practice.  Please generate a new key for the patient.","Print Key");
            return;
        }
        formData.createdMMLKey = formData.currentRow.mmlKey;
        if (formData.currentRow.status == "Connected")
        {
            await printInstructions();
            return;
        }
        await printConnectionLetter();
    }

    function isLetterConfigured() {
        if (!formData.letterTemplateId) {
            popAlert("The letter to print has not been configured yet.","Print Key");
            return false;
        } 
        return true;       
    }

    async function printConnectionLetter() {
        // Print the key
        const encounterDate = "";
        const printData = `MMLKey.htm|isp_get_pdf_report_letter|${formData.letterTemplateId}|${props.demographics.patientId}|${props.demographics.patientEid}|${encounterDate}|MyMedicalLocker Key^${formData.createdMMLKey}`;
        const windowId = AppScreens.PDFWindow;
        const windowUrl = `/officemd/datapages/pdf_report.asp?txtReportSection=Patient Report&txtReportInput=${printData}`;
        openWindowFromVue(windowId, windowUrl, { noTimestamp: "Y" });

        // Audit the print
        const post_data = <IMMLAuditPrintInputs> {
            patientId: props.demographics.patientId,
            mmlKey: formData.createdMMLKey
        }
        const resp = await MML.AuditPrint(post_data);
        if (!resp?.successful) {
            await popAlert(resp?.msg ?? "Failed to audit the print","Print Key",ModalStyles.danger);
        }

    }

    async function onDeleteKey() {
        if (!formData.currentRow) {
            popAlert("Please select a key to delete","Delete Key");
            return;
        }
        formData.deleteKeyReason = "";
        formData.showReason = true;
    }

    async function onDeleteKeyContinued() {
        if (!formData.currentRow) {
            popAlert("Please select a key to delete","Delete Key");
            return;
        }

        if (!formData.deleteKeyReason) {
            popAlert("Please enter a reason.","Delete Key");
            return;
        }

        const post_data = <IMMLDeleteKeyInputs> {
            patientId: props.demographics.patientId,
            mmlKey: formData.currentRow.mmlKey,
            comment: formData.deleteKeyReason
        }
        const resp = await MML.DeleteKey(post_data);
        if (!resp?.successful) {
            await popAlert(resp?.msg ?? "Failed to delete the key","Delete Key",ModalStyles.danger);
        }
        formData.currentRow.status = "Deleted";
        onHideReason();
    }

    function onHideReason() {
        formData.showReason = false;
    }

    async function onAuditKey() {
        if (!formData.currentRow) {
            popAlert("Please select a key to audit","Audit Key");
            return;
        }

        const post_data = <IMMLAuditListInputs> {
            patientId: props.demographics.patientId,
            mmlKey: formData.currentRow.mmlKey
        }
        const result = await MML.AuditList(post_data);
        if (!result) {
            popAlert("Failed to retrieve the audit information","Adudit Key",ModalStyles.danger);
            return;
        }
        formData.auditList = result;
        formData.showAudit = true;
    }

    function onHideAudit() {
        formData.showAudit = false;
    }

    async function onConnectPatientStart() {
        formData.verbalConnect = false;

        if (hasFraud.value) {
            formData.showCreditCardFraudDialog = true;
        }
        else {
            formData.showVerbalConnect = true;
        }        
    }

    async function printInstructions() {
        if (!formData.letterInstructionsId) {
            popAlert("The letter to print has not been configured yet.","Print Key");
            return;
        }    
        const encounterDate = "";
        const printData = `MMLKey.htm|isp_get_pdf_report_letter|${formData.letterInstructionsId}|${props.demographics.patientId}|${props.demographics.patientEid}|${encounterDate}|MyMedicalLocker Key^${formData.createdMMLKey}`;
        const windowId = AppScreens.PDFWindow;
        const windowUrl = `/officemd/datapages/pdf_report.asp?txtReportSection=Patient Report&txtReportInput=${printData}`;
        openWindowFromVue(windowId, windowUrl, { noTimestamp: "Y" });
    }

    function onHideVerbalConnect() {
        formData.showVerbalConnect = false;
    }

    async function onHideCreditCardFraud() {
        formData.showCreditCardFraudDialog = false;
    }    

    async function onConnectContinued() {
        onHideVerbalConnect();
        onShowConnectPatient();
    }

    function onShowConnectPatient(clearLastResults?: boolean) {
        if (clearLastResults == undefined)
            clearLastResults = true;
        if (clearLastResults)
            clearConnection();
        formData.showConnectPatient = true;
    }

    function clearConnection() {
        formData.adminConnectionType = MMLAdminConnectionType.CreateEmail;
        formData.requestedBy = undefined;
        formData.createEmail = undefined;
        formData.createPhone = undefined;
        formData.generateEmail = undefined;
        formData.generatePhone = undefined;
        formData.createdMMLKey = undefined;
    }

    function onHideConnectPatient() {
        formData.showConnectPatient = false;
    }

    async function onConnectTypeChange() {
        formData.createEmail = undefined;
        formData.createPhone = undefined;
        await nextTick();
        switch (formData.adminConnectionType) {
            case MMLAdminConnectionType.CreateEmail: createEmailInput.value.focus(); break;
            case MMLAdminConnectionType.CreatePhone: createPhoneInput.value.focus(); break;
            case MMLAdminConnectionType.GenerateEmail: generateEmailInput.value.focus(); break;
            case MMLAdminConnectionType.GeneratePhone: generatePhoneInput.value.focus(); break;
            default: break;
        }
    }

    async function onConnectNow() {
        if (!formData.requestedBy) {
            popAlert("Please enter who requested the connection","Connect");
            return;            
        }

        switch (formData.adminConnectionType) {
            case MMLAdminConnectionType.CreateEmail: 
                await sendEmailToken(); 
                break;
            case MMLAdminConnectionType.CreatePhone: 
                await sendPhoneToken(); 
                break;
            case MMLAdminConnectionType.GenerateEmail: 
                await sendEmailKey(); 
                break;
            case MMLAdminConnectionType.GeneratePhone: 
                await sendPhoneKey();
                break;
            case MMLAdminConnectionType.GenerateLetter: 
                await generateKeyandPrint();
                break;
            default: break;
        }
    }

    async function sendEmailKey() {
        if (!formData.generateEmail) {
            popAlert("Please enter an email address","Connect");
            return;            
        }
        if (!Validation.isValidEmail(formData.generateEmail)) {
            popAlert("Please enter an valid email address","Connect");
            return;            
        }

        const post_data = <IMMLSendKeyInputs> {
            patientId: props.demographics.patientId,
            requestedBy: formData.requestedBy,
            sendType: MMLAdminSendType.EmailKey,
            email: formData.generateEmail
        }
        const resp = await MML.SendKey(post_data);
        if (!resp?.successful) {
            await popAlert(resp?.msg ?? "Failed to send the token.","Send Email Key",ModalStyles.danger);
            return;
        }
        onHideConnectPatient();
        popAlert("Account creation information has been sent to the patient.","Send Email Key");
    }

    async function sendPhoneKey() {
        if (!formData.generatePhone) {
            popAlert("Please enter a phone number","Connect");
            return;            
        }
        if (!Validation.isValidPhone(formData.generatePhone)) {
            popAlert("Please enter a valid phone number","Connect");
            return;            
        }

        const post_data = <IMMLSendKeyInputs> {
            patientId: props.demographics.patientId,
            requestedBy: formData.requestedBy,
            sendType: MMLAdminSendType.PhoneKey,
            phone: formData.generatePhone
        }
        const resp = await MML.SendKey(post_data);
        if (!resp?.successful) {
            await popAlert(resp?.msg ?? "Failed to send the token.","Send Phone Key",ModalStyles.danger);
            return;
        }
        onHideConnectPatient();
        popAlert("Account creation information has been sent to the patient.","Send Phone Key");
    }

    async function sendEmailToken() {
        if (!formData.createEmail) {
            popAlert("Please enter an email address","Connect");
            return;            
        }
        if (!Validation.isValidEmail(formData.createEmail)) {
            popAlert("Please enter an valid email address","Connect");
            return;            
        }

        const post_data = <IMMLSendTokenInputs> {
            patientId: props.demographics.patientId,
            requestedBy: formData.requestedBy,
            sendType: MMLAdminSendType.EmailToken,
            email: formData.createEmail
        }
        const resp = await MML.SendToken(post_data);
        if (!resp?.successful) {
            await popAlert(resp?.msg ?? "Failed to send the token.","Send Email Token",ModalStyles.danger);
            return;
        }

        onShowTokenEntry();
        onHideConnectPatient();
    }

    async function sendPhoneToken() {
        if (!formData.createPhone) {
            popAlert("Please enter a phone number","Connect");
            return;            
        }
        if (!Validation.isValidPhone(formData.createPhone)) {
            popAlert("Please enter a valid phone number","Connect");
            return;            
        }

        const post_data = <IMMLSendTokenInputs> {
            patientId: props.demographics.patientId,
            requestedBy: formData.requestedBy,
            sendType: MMLAdminSendType.PhoneToken,
            phone: formData.createPhone
        }
        const resp = await MML.SendToken(post_data);
        if (!resp?.successful) {
            await popAlert(resp?.msg ?? "Failed to send the token.","Send Phone Token",ModalStyles.danger);
            return;
        }

        onShowTokenEntry();
        onHideConnectPatient();
    }

    async function generateKeyandPrint() {
        if (!isLetterConfigured())
            return;

        const post_data = <IMMLCreateKeyOnlyInputs> {
            patientId: props.demographics.patientId,
            requestedBy: formData.requestedBy
        }
        const resp = await MML.CreateKeyOnly(post_data);
        if (!resp?.successful) {
            await popAlert("Failed to generate the connection key for the letter.","Print Letter",ModalStyles.danger);
        }
        formData.createdMMLKey = resp?.mmlKey;
        await printConnectionLetter();
        onHideConnectPatient();
    }

    function onShowTokenEntry() {
        formData.token = undefined;
        formData.showTokenEntry = true;
    }

    function onHideTokenEntry() {
        formData.showTokenEntry = false;
    }

    async function onSendTokenBackup() {
        onHideTokenEntry();
        onShowConnectPatient(false);
    }

    async function onConnectPatient() {
        if (!formData.token) {
            popAlert("Please enter a token","Connect Patient");
            return;            
        }

        const post_data = <IMMLCreateAccountInputs> {
            idType: "patient",
            identifier: props.demographics.patientId,
            requestedBy: formData.requestedBy,
            mmlType: formData.adminConnectionType == MMLAdminConnectionType.CreateEmail ? "E" : "P",
            email: formData.createEmail,
            phone: formData.createPhone,
            token: formData.token?.toString()
        }
        const resp = await MML.CreateAccount(post_data);
        if (!resp?.successful) {
            await popAlert(resp?.responseMessage ?? "Failed to create the account.","Connect Patient",ModalStyles.danger);
            return;
        }
        formData.createdMMLKey = resp.mmlKey;
        if (resp.responseMessage) {
            await popAlert(resp?.responseMessage,"Connect Patient");
        }
        onHideTokenEntry();
        onShowPrintInstructions();
        onRefresh();
    }

    async function onRefresh(){
        const promiseConnectionType = MML.GetConnectionType(props.demographics.patientId);
        const promiseKeyList = MML.KeyList(props.demographics.patientId);
        await Promise.all([promiseConnectionType, promiseKeyList]).then(function(data) {
            if (data[0])
                formData.connectionType = data[0];
            if (data[1])
                formData.keyList = data[1];
        });
    }

    function onShowPrintInstructions() {
        formData.showPrintInstructions = true;
    }

    function onHidePrintInstructions() {
        formData.showPrintInstructions = false;
    }

</script>

<template>
    <ResizeMonitor @resize="windowResize" />
    <div class="row mt-1">
        <div class="col-auto pe-0">
            <MMLIcon  
                :connectionType="formData.connectionType" />
        </div>
        <div class="col ps-1 isalus-patient-header">
            {{  $props.demographics.patientId }}-{{  $props.demographics.lastName }}, {{ $props.demographics.firstName }}{{ patientMiddleInitial }}{{ patientBirthDate }}{{ patientGender }}
        </div>
    </div>
    <div class="row mt-2">
        <div class="col">
            <ChoiceTable 
                ref="keyTable"
                :columns="keyColumns" 
                :data="formData.keyList" 
                :show-filters="false" 
                :allowSorting="false"
                :height="formData.windowHeight"
                fontSize="smaller"
                :rowHighlight="TableRowHighlight.onSingleClick"
                :showPaginationButtonsBelowChoiceTable="true"
                @rowclick="onRowClick"
                :key-value-data-member="'recordId'" />
        </div>
    </div>
    <div class="row mt-2">
        <div class="col">
            <ActionButtonV2 
                :action="ActionButtonStyle.Print" 
                text="Print Key"
                spinText="Printing"
                tooltip="Print the selected key"
                :disabled="keyButtonDisabled"
                :size="ButtonDisplaySize.Small" 
                :performAction="onPrintKey"
                :endingPadding="1" />           
            <ActionButtonV2 
                :action="ActionButtonStyle.Delete" 
                text="Revoke Access"
                spinText="Revoking"
                tooltip="Revoke access by marking the selected key as deleted."
                :disabled="keyButtonDisabled"
                :size="ButtonDisplaySize.Small" 
                :performAction="onDeleteKey"
                :endingPadding="1" />          
            <ActionButtonV2 
                :action="ActionButtonStyle.Audit" 
                text="Audit Key"
                spinText="Auditing"
                tooltip="Audit the selected key"
                :disabled="keyButtonDisabled"
                :size="ButtonDisplaySize.Small" 
                :performAction="onAuditKey"
                :endingPadding="1" />
            <ActionButtonV2 
                :action="ActionButtonStyle.Refresh" 
                text=""
                spinText=""
                tooltip="Refresh the current list"
                :size="ButtonDisplaySize.Small" 
                :performAction="onRefresh" />               
        </div>
        <div class="col-auto">
            <ActionButtonV2 
                :action="ActionButtonStyle.Link" 
                text="Connect Patient"
                spinText="Connecting"
                tooltip="Create a MyMedicalLocker Account"
                :size="ButtonDisplaySize.Small" 
                :performAction="onConnectPatientStart" />               
        </div>
    </div>
    <ModalDialog :show="formData.showReason" :size="ModalSize.Large" @hide="onHideReason">
        <template #title>Delete Key</template>
        <div class="row">
            <div class="col label-isalus-small">
                Reason
            </div>
            <col class="col-width-100">            
            <div class="col">
                <TextInput 
                    v-model:text-value="formData.deleteKeyReason" 
                    :rows="5"
                    :maxLength="1500"
                    :cssFramework = "'vanilla'" />
            </div>
            <col class="col-width-100">            
            <div class="col float-end">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Check" 
                    text="OK"
                    spinText="Deleting"
                    tooltip="Delete the key with the reason"
                    :disabled="!formData.deleteKeyReason"
                    :size="ButtonDisplaySize.Small" 
                    :performAction="onDeleteKeyContinued" />         
            </div>
        </div>  
    </ModalDialog>
    <ModalDialog :show="formData.showAudit" :size="ModalSize.Large" @hide="onHideAudit">
        <template #title>Audit Key - {{ formData.currentRow?.mmlKey }}</template>
        <ChoiceTable 
            :columns="auditColumns" 
            :data="formData.auditList" 
            :show-filters="false" 
            :allowSorting="false"
            :height="300"
            fontSize="smaller"
            :key-value-data-member="'auditId'" />
    </ModalDialog>
    <ModalDialog :show="formData.showCreditCardFraudDialog" :size="ModalSize.Medium" :closeOnClickOutside="false" @hide="onHideCreditCardFraud">
        <template #title>Warning - Credit Card Fraud</template>
        <div class="col isalus-section-header">
            {{ fraudRecord?.comment }}
        </div>
        <div class="col-auto float-end">
            <ActionButtonV2 
                :action="ActionButtonStyle.Close" 
                text="Close"
                spinText="Closing"
                :size="ButtonDisplaySize.Small" 
                :performAction="onHideCreditCardFraud" />
        </div>
    </ModalDialog>
    <ModalDialog :show="formData.showVerbalConnect" :size="ModalSize.Large" :closeOnClickOutside="false" @hide="onHideVerbalConnect">
        <template #title>Verify Patient</template>
        <div class="row">
            <div class="col isalus-patient-header">
                {{  $props.demographics.lastName }}, {{ $props.demographics.firstName }}{{ patientMiddleInitial }}{{ patientBirthDate }}{{ patientGender }}
            </div>
            <template v-if="$props.demographics.address1">
                <col class="col-width-100">
                <div class="col isalus-section-header">
                    {{  $props.demographics.address1 }}
                </div>
            </template>
            <template v-if="$props.demographics.address2">
                <col class="col-width-100">
                <div class="col isalus-section-header">
                    {{  $props.demographics.address2 }}
                </div>
            </template>
            <template v-if="$props.demographics.city || $props.demographics.state || $props.demographics.zipCode">
                <col class="col-width-100">
                <div class="col isalus-section-header">
                    {{  $props.demographics.city }}, {{ $props.demographics.state }} {{ $props.demographics.zipCode }}
                </div>
            </template>
            <template v-if="$props.demographics.email">
                <col class="col-width-100">
                <div class="col-2 pe-0 isalus-label-secondary">
                    email
                </div>
                <div class="col isalus-section-header">
                    {{ $props.demographics.email }}
                </div>
            </template>
            <template v-if="$props.demographics.phoneHome">
                <col class="col-width-100">
                <div class="col-2 pe-0 isalus-label-secondary">
                    home
                </div>
                <div class="col isalus-section-header">
                    {{ $props.demographics.phoneHome }}
                </div>
            </template>
            <template v-if="$props.demographics.phoneWork">
                <col class="col-width-100">
                <div class="col-2 pe-0 isalus-label-secondary">
                    work
                </div>
                <div class="col isalus-section-header">
                    {{ $props.demographics.phoneWork }}
                </div>
            </template>
            <template v-if="$props.demographics.phoneOther">
                <col class="col-width-100">
                <div class="col-2 pe-0 isalus-label-secondary">
                    other
                </div>
                <div class="col isalus-section-header">
                    {{ $props.demographics.phoneOther }}
                </div>
            </template>
        </div>
        <div class="row mt-2">
            <div class="col border border-dark bg-danger text-white">
                Please VERBALLY verify the information shown above with the patient before continuing.
            </div>
        </div>
        <div class="row mt-2">
            <div class="col">
                <Checkbox v-model="formData.verbalConnect" :title="'Verbally verfiy information with patient'"/>                         
                <span label class="ps-1 pe-3">I Verify that I am on the correct patient</span>
            </div>
            <div class="col-auto float-end">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Check" 
                    text="OK"
                    spinText="Verified"
                    tooltip="I have verbally verified the information with the patient and will continue with next step"
                    :disabled="!formData.verbalConnect"
                    :size="ButtonDisplaySize.Small" 
                    :performAction="onConnectContinued" />
            </div>
        </div>
    </ModalDialog>
    <ModalDialog :show="formData.showConnectPatient" :size="ModalSize.Large" :closeOnClickOutside="false" @hide="onHideConnectPatient">
        <template #title>Connect Patient to MyMedicalLocker</template>
        <div class="row">
            <div class="col-3 label-isalus-small mt-2">
                Requested By
            </div>
            <div class="col">
                <TextInput 
                    v-model:textValue="formData.requestedBy" 
                    :cssFramework="'vanilla'"  
                    :maxLength="100"
                    placeholder="Who requested the account" />
            </div>
        </div>
        <div class="row mt-2">
            <div class="col isalus-patient-header">
                Create and Connect Account
            </div>
            <div class="col mt-1 text-danger label-isalus-small">
                Practice/Patient Interaction Required
            </div>
            <col class="col-width-100">
            <div class="col label-isalus-small">
                A token will be sent to the patient and that token must be read back to you by the patient and entered back into this screen. The patient's MyMedicalLocker account will be created and automatically connected to their chart.
            </div>
            <col class="col-width-100 mt-1">
            <div class="col-3">
                <input class="form-check-input" type="radio" name="adminConnectType"  :value="MMLAdminConnectionType.CreateEmail" v-model="formData.adminConnectionType" @change="onConnectTypeChange()">
                <label class="form-check-label label-isalus-small ms-1" for="adminConnectType">Email Address</label>
            </div>
            <div class="col">
                <EmailInput 
                    ref="createEmailInput"
                    v-model="formData.createEmail" 
                    :cssFramework="'vanilla'" 
                    :disabled="formData.adminConnectionType != MMLAdminConnectionType.CreateEmail"
                    :maxLength="50" />                
            </div>
            <col class="col-width-100 mt-1">
            <div class="col-3">
                <input class="form-check-input" type="radio" name="adminConnectType" ref="connectPhone" :value="MMLAdminConnectionType.CreatePhone" v-model="formData.adminConnectionType"  @change="onConnectTypeChange()">
                <label class="form-check-label label-isalus-small ms-1" for="adminConnectType">Phone</label>
            </div>
            <div class="col">
                <PhoneInput 
                    ref="createPhoneInput"
                    v-model="formData.createPhone"
                    :cssFramework="'vanilla'" 
                    :disabled="formData.adminConnectionType != MMLAdminConnectionType.CreatePhone"/> 
            </div>
            <col class="col-width-100 mt-2">
            <div class="col isalus-patient-header">
                Generate Connect Key
            </div>
            <div class="col mt-1 text-danger label-isalus-small">
                No Practice/Patient Interaction
            </div>
            <col class="col-width-100">
            <div class="col label-isalus-small">
                A connection key will be generated and sent to the patient.  The patient must manually create a MyMedicalLocker account, enter the connection token, and verify data about themselves to connect their account to their chart.
            </div>
            <col class="col-width-100 mt-1">
            <div class="col-3">
                <input class="form-check-input" type="radio" name="adminConnectType"  :value="MMLAdminConnectionType.GenerateEmail" v-model="formData.adminConnectionType" @change="onConnectTypeChange()">
                <label class="form-check-label label-isalus-small ms-1" for="adminConnectType">Email Address</label>
            </div>
            <div class="col">
                <EmailInput 
                    ref="generateEmailInput"
                    v-model="formData.generateEmail" 
                    :cssFramework="'vanilla'"
                    :disabled="formData.adminConnectionType != MMLAdminConnectionType.GenerateEmail" 
                    :maxLength="50" />               
            </div>
            <col class="col-width-100 mt-1">
            <div class="col-3">
                <input class="form-check-input" type="radio" name="adminConnectType" ref="connectPhone" :value="MMLAdminConnectionType.GeneratePhone" v-model="formData.adminConnectionType"  @change="onConnectTypeChange()">
                <label class="form-check-label label-isalus-small ms-1" for="adminConnectType">Phone</label>
            </div>
            <div class="col">
                <PhoneInput 
                    ref="generatePhoneInput"
                    v-model="formData.generatePhone"
                    :cssFramework="'vanilla'" 
                    :disabled="formData.adminConnectionType != MMLAdminConnectionType.GeneratePhone"/> 
            </div>
            <col class="col-width-100 mt-1">
            <div class="col-3">
                <input class="form-check-input" type="radio" name="adminConnectType" ref="connectLetter" :value="MMLAdminConnectionType.GenerateLetter" v-model="formData.adminConnectionType"  @change="onConnectTypeChange()">
                <label class="form-check-label label-isalus-small ms-1" for="adminConnectType">Letter</label>
            </div>
        </div>
        <div class="row">
            <div class="col"></div>
            <div class="col-auto">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Check" 
                    :text="connectButtonText"
                    :spinText="connectButtonSpinText"
                    :tooltip="connectButtonTooltip"
                    :size="ButtonDisplaySize.Small" 
                    :performAction="onConnectNow" />                
            </div>
        </div>
    </ModalDialog>
    <ModalDialog :show="formData.showTokenEntry" :size="ModalSize.Large" :closeOnClickOutside="false" @hide="onHideTokenEntry">
        <template #title>Connect Patient to MyMedicalLocker</template>
        <div class="row">
            <div class="col isalus-patient-header mt-2">
                Token Sent To: {{ informationSentTo }}
            </div>
            <col class="col-width-100 mt-1">
            <div class="col mt-2 label-isalus-small">
                Please enter the Token sent to the patient
            </div>
            <col class="col-width-100 mt-1">
            <div class="col">
                <TextInput 
                    v-model:text-value="formData.token" 
                    :normalizer="Conversions.TextToNumber" 
                    :validator="Validation.InRangeFunction(0, 9999)"
                    :acceptCharacter="Validation.LimitToNumberCharacters"
                    :maxLength="4"
                    :cssFramework = "'vanilla'">
                    <template #LeftAddon>
                        #
                    </template>
                </TextInput>
            </div>
        </div>
        <div class="row mt-2">
            <div class="col">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Backward" 
                    text="Back"
                    spinText="Backing up"
                    tooltip="Go back to the previous screen"
                    :size="ButtonDisplaySize.Small" 
                    :performAction="onSendTokenBackup" />
            </div>
            <div class="col-auto">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Link" 
                    text="Connect Patient"
                    spinText="Connecting"
                    tooltip="Create/Connect the patient to an MML account."
                    :size="ButtonDisplaySize.Small" 
                    :performAction="onConnectPatient" />
            </div>
        </div>
    </ModalDialog>
    <ModalDialog :show="formData.showPrintInstructions" :size="ModalSize.Large" :closeOnClickOutside="false" @hide="onHidePrintInstructions">
        <template #title>Patient has been successfully connected!</template>
        <ActionButtonV2 
            :action="ActionButtonStyle.Link" 
            text="Print Instructions"
            spinText="Print"
            tooltip="Print the instructions for the patient."
            :size="ButtonDisplaySize.Small" 
            :performAction="printInstructions" />
    </ModalDialog>
    <ChoiceStateMessageSink />    
</template>
